// TypingBox.jsx
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import TypingStats from "./TypingStats";

export default function TypingBox({
  lesson,
  wpm,
  accuracy,
  errors,
  time,
  setWpm,
  setAccuracy,
  setErrors,
  setTime,
  setCpm,
  setKph,
  onPrev,
  onNext,
}) {
  const code = lesson?.code || "";
  const [input, setInput] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const containerRef = useRef(null);
  const hiddenInputRef = useRef(null);
  const cursorRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const lastActivityTimeRef = useRef(null);

  // Maximum allowed incorrect characters beyond current position
  const MAX_INCORRECT_AHEAD = 20;

  // Memoize code characters to prevent re-splitting on every render
  const codeChars = useMemo(() => code.split(""), [code]);

  // Check if it's mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Reset typing whenever lesson changes
  useEffect(() => {
    setInput("");
    setIsCompleted(false);
    setStartTime(null);
    setElapsedTime(0);
    lastActivityTimeRef.current = null;
    setIsPaused(false);
    setWpm(0);
    setAccuracy(100);
    setErrors(0);
    setTime(0);
    setCpm(0);
    setKph(0);

    if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);

    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }

    // Focus hidden input on mobile when lesson changes
    if (isMobile && hiddenInputRef.current) {
      setTimeout(() => {
        hiddenInputRef.current?.focus();
      }, 500);
    }
  }, [lesson, isMobile]);

  // Real-time timer - OPTIMIZED: Moved outside metrics calculation
  useEffect(() => {
    if (startTime && !isCompleted && !isPaused) {
      timeIntervalRef.current = setInterval(() => {
        const currentTime = (Date.now() - startTime) / 1000;
        setElapsedTime(currentTime);
        setTime(currentTime.toFixed(2));
      }, 100);
    } else {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    }

    return () => {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    };
  }, [startTime, isCompleted, isPaused, setTime]);

  // Handle inactivity
  useEffect(() => {
    if (startTime && !isCompleted && !isPaused && lastActivityTimeRef.current) {
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);

      inactivityTimeoutRef.current = setTimeout(() => {
        setIsPaused(true);
      }, 10000);
    }

    return () => {
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    };
  }, [startTime, isCompleted, isPaused, lastActivityTimeRef.current]);

  // ✅ OPTIMIZED: Real-time metric calculation with batched updates
  useEffect(() => {
    if (!input) {
      // Batch all state updates together
      setWpm(0);
      setAccuracy(100);
      setErrors(0);
      setCpm(0);
      setKph(0);
      return;
    }

    // Calculate errors count efficiently
    let errorsCount = 0;
    for (let i = 0; i < input.length; i++) {
      if (input[i] !== code[i]) {
        errorsCount++;
      }
    }
    
    const accuracyVal = Math.max(0, Math.round(((input.length - errorsCount) / input.length) * 100));

    if (elapsedTime > 0 && !isPaused) {
      const timeElapsedMinutes = elapsedTime / 60;
      const calculatedWpm = Math.round((input.length / 5) / timeElapsedMinutes) || 0;
      const calculatedCpm = Math.round((input.length / elapsedTime) * 60) || 0;
      const calculatedKph = Math.round((input.length / elapsedTime) * 3600) || 0;

      setWpm(calculatedWpm);
      setCpm(calculatedCpm);
      setKph(calculatedKph);
    } else {
      setWpm(0);
      setCpm(0);
      setKph(0);
    }

    setAccuracy(accuracyVal);
    setErrors(errorsCount);
  }, [input, elapsedTime, code, isPaused, setWpm, setCpm, setKph, setAccuracy, setErrors]);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    lastActivityTimeRef.current = now;

    if (isPaused) {
      setIsPaused(false);
      setStartTime((prev) => {
        if (prev && lastActivityTimeRef.current) {
          const pauseDuration = now - (lastActivityTimeRef.current + 10000);
          return prev + pauseDuration;
        }
        return prev;
      });
    }
  }, [isPaused]);

  // Enhanced mobile focus handling
  const handleContainerClick = useCallback(() => {
    if (!isCompleted) {
      // Focus hidden input to trigger mobile keyboard
      if (isMobile && hiddenInputRef.current) {
        hiddenInputRef.current.focus();
        hiddenInputRef.current.setSelectionRange(
          hiddenInputRef.current.value.length,
          hiddenInputRef.current.value.length
        );
      } else if (containerRef.current) {
        containerRef.current.focus();
      }
    }
  }, [isCompleted, isMobile]);

  // Handle input from hidden input (for mobile) - OPTIMIZED
  const handleHiddenInputChange = useCallback((e) => {
    if (isCompleted) return;
    
    const value = e.target.value;
    
    // Only process if there's new input (not deletion)
    if (value.length > input.length) {
      const newChar = value[value.length - 1];
      
      handleUserActivity();

      // Calculate incorrect characters efficiently
      let correctChars = 0;
      for (let i = 0; i < input.length; i++) {
        if (input[i] === code[i]) correctChars++;
      }
      const incorrectAhead = input.length - correctChars;
      
      if (incorrectAhead >= MAX_INCORRECT_AHEAD) {
        if (input.length < code.length && newChar === code[input.length]) {
          setInput(prev => prev + newChar);
        }
        e.target.value = input;
        return;
      }

      // Handle special characters from mobile keyboard
     if (newChar === '\n') {
  setInput(prev => prev + "\n");
} else if (newChar === '\t') {
  setInput(prev => prev + "    ");
} else if (newChar === ' ') {
  // ✅ Allow space to behave like Enter only on mobile when next expected char is newline
  if (isMobile && code[input.length] === '\n') {
    setInput(prev => prev + "\n");
  } else {
    setInput(prev => prev + " ");
  }
}
 else if (newChar && newChar.length === 1) {
        if (!startTime) {
          const now = Date.now();
          setStartTime(now);
          lastActivityTimeRef.current = now;
          setElapsedTime(0);
        }
        
        setInput(prev => prev + newChar);
      }

      if (input.length + 1 >= code.length) {
        setIsCompleted(true);
        if (startTime) {
          const finalTime = (Date.now() - startTime) / 1000;
          setElapsedTime(finalTime);
          setTime(finalTime.toFixed(2));
        }
      }
    } else if (value.length < input.length) {
      setInput(prev => prev.slice(0, -1));
    }
    
    e.target.value = input;
  }, [isCompleted, handleUserActivity, startTime, input, code, setTime]);

  // Handle backspace specifically for hidden input
  const handleHiddenInputKeyDown = useCallback((e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      setInput(prev => prev.slice(0, -1));
    }
  }, []);

  // ✅ OPTIMIZED: Original keyboard handler (for desktop)
  const handleKeyDown = useCallback((e) => {
    if (isCompleted) return;
    
    handleUserActivity();
    e.preventDefault();

    if (e.key === "Backspace") {
      if (input.length > 0) {
        setInput(prev => prev.slice(0, -1));
      }
      return;
    }

    // Calculate incorrect characters efficiently
    let correctChars = 0;
    for (let i = 0; i < input.length; i++) {
      if (input[i] === code[i]) correctChars++;
    }
    const incorrectAhead = input.length - correctChars;
    
    if (incorrectAhead >= MAX_INCORRECT_AHEAD) {
      if (e.key.length === 1 && input.length < code.length && e.key === code[input.length]) {
        setInput(prev => prev + e.key);
      }
      return;
    }

    if (e.key === "Enter") {
      setInput(prev => prev + "\n");
    } else if (e.key === "Tab") {
      setInput(prev => prev + "    ");
    } else if (e.key === " ") {
      setInput(prev => prev + " ");
    } else if (e.key.length === 1) {
      if (!startTime) {
        const now = Date.now();
        setStartTime(now);
        lastActivityTimeRef.current = now;
        setElapsedTime(0);
      }
      
      setInput(prev => prev + e.key);
    }

    if (input.length + 1 >= code.length) {
      setIsCompleted(true);
      if (startTime) {
        const finalTime = (Date.now() - startTime) / 1000;
        setElapsedTime(finalTime);
        setTime(finalTime.toFixed(2));
      }
    }
  }, [isCompleted, handleUserActivity, startTime, input, code, setTime]);

  // Auto-scroll to keep cursor visible - OPTIMIZED with throttling
  useEffect(() => {
    if (cursorRef.current && containerRef.current && !isCompleted) {
      const scrollTimer = setTimeout(() => {
        const cursorElement = cursorRef.current;
        const container = containerRef.current;

        if (cursorElement && container) {
          const cursorRect = cursorElement.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          const scrollMargin = isMobile ? 100 : 150;
          const visibleTop = containerRect.top + scrollMargin;
          const visibleBottom = containerRect.bottom - scrollMargin;

          if (cursorRect.bottom > visibleBottom) {
            const scrollAmount = cursorRect.bottom - visibleBottom + 20;
            container.scrollBy({ top: scrollAmount, behavior: "smooth" });
          } else if (cursorRect.top < visibleTop) {
            const scrollAmount = cursorRect.top - visibleTop - 20;
            container.scrollBy({ top: scrollAmount, behavior: "smooth" });
          }
        }
      }, 16); // ~60fps throttling

      return () => clearTimeout(scrollTimer);
    }
  }, [input.length, isCompleted, isMobile]);

  // Enhanced focus management for mobile
  useEffect(() => {
    const container = containerRef.current;
    if (container && !isCompleted) {
      if (!isMobile) {
        container.focus();
      }
      
      const handleClick = handleContainerClick;
      container.addEventListener("click", handleClick);
      
      return () => container.removeEventListener("click", handleClick);
    }
  }, [isCompleted, isMobile, handleContainerClick]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    };
  }, []);

  // Calculate how many incorrect characters are there - OPTIMIZED
  const { correctChars, incorrectAhead, isInputBlocked } = useMemo(() => {
    let correctChars = 0;
    for (let i = 0; i < input.length; i++) {
      if (input[i] === code[i]) correctChars++;
    }
    const incorrectAhead = input.length - correctChars;
    const isInputBlocked = incorrectAhead >= MAX_INCORRECT_AHEAD;
    
    return { correctChars, incorrectAhead, isInputBlocked };
  }, [input, code]);

  // Completed view
  if (isCompleted) {
    return (
      <div className="relative flex flex-col w-full h-screen md:h-[90vh]">
        <div className="bg-black text-white p-6 rounded-lg w-full h-full overflow-auto font-mono flex items-center justify-center">
          <TypingStats
            metrics={{
              wpm,
              accuracy,
              errors,
              time: elapsedTime.toFixed(2),
              cpm: Math.round((input.length / elapsedTime) * 60) || 0,
              kph: Math.round((input.length / elapsedTime) * 3600) || 0,
            }}
            onRestart={() => {
              setInput("");
              setIsCompleted(false);
              setStartTime(null);
              setElapsedTime(0);
              lastActivityTimeRef.current = null;
              setIsPaused(false);
              setWpm(0);
              setAccuracy(100);
              setErrors(0);
              setTime(0);
              setCpm(0);
              setKph(0);

              if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
              if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);

              if (containerRef.current) {
                containerRef.current.scrollTop = 0;
              }
              if (hiddenInputRef.current) {
                hiddenInputRef.current.value = "";
                setTimeout(() => hiddenInputRef.current?.focus(), 100);
              }
            }}
            onPrev={onPrev}
            onNext={onNext}
          />
        </div>
      </div>
    );
  }

  // Typing area
  return (
    <div className="relative flex flex-col w-full h-screen md:h-[90vh]">
      {/* Hidden input for mobile keyboard */}
    {isMobile && (
  <textarea
    ref={hiddenInputRef}
    id="typing-input-mobile"
    name="typing-input"
    className="absolute opacity-0 h-0 w-0"
    onChange={handleHiddenInputChange}
    onKeyDown={handleHiddenInputKeyDown}
    value={input}
    autoComplete="off"
    autoCorrect="off"
    autoCapitalize="off"
    spellCheck="false"
    rows={1}
  />
)}


      {isPaused && (
        <div className="absolute top-4 right-4 bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-medium z-10 animate-pulse">
          ⏸️ Timer Paused
        </div>
      )}

      {isInputBlocked && (
        <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium z-10 animate-pulse">
          ❌ Too many errors! Use Backspace to correct
        </div>
      )}

      {/* Mobile tap instruction */}
      {isMobile && input.length === 0 && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium z-10 animate-bounce">
          👆 Tap anywhere to start typing
        </div>
      )}

      <div
        tabIndex={0}
        ref={containerRef}
        onKeyDown={!isMobile ? handleKeyDown : undefined}
        onClick={handleContainerClick}
        className="bg-black text-gray-400 p-2 rounded-lg min-h-[400px] md:h-[90vh] w-full overflow-auto font-mono text-sm md:text-lg outline-none whitespace-pre-wrap relative custom-scrollbar leading-relaxed touch-manipulation cursor-pointer"
        style={{ 
          scrollBehavior: "smooth", 
          lineHeight: "1.6",
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent"
        }}
      >
        <div className="text-yellow-500 mb-3 text-xs md:text-sm">
          <div>␣ = Press Space(␣)</div>
          <div>1 tab(⇥) = 4 Spaces(␣)</div>
          <div>Note: Don't make more than 15 character mistake, or you will regret it later.</div>
        </div>
        <div className="whitespace-pre-wrap break-all">
          {codeChars.map((char, i) => {
            const isTyped = i < input.length;
            const isCorrect = isTyped && input[i] === char;
            const isCursor = i === input.length && !isCompleted;
            
            return (
              <span
                key={i}
                className={`${
                  isTyped 
                    ? isCorrect ? "text-green-400" : "text-red-500 bg-red-900 bg-opacity-30"
                    : "text-gray-400"
                } transition-colors duration-100 break-all inline`}
                ref={isCursor ? cursorRef : null}
              >
                {isCursor ? (
                  <span className="bg-white text-black animate-pulse px-0.5 rounded">
                    {char === " " ? "␣" : char || "|"}
                  </span>
                ) : char === " " ? (
                  <span className="opacity-70">␣</span>
                ) : char === "\n" ? (
                  <br />
                ) : char === "\t" ? (
                  <span className="opacity-70">    </span>
                ) : (
                  char
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}